import { useEffect, useState, lazy, Suspense, useCallback } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { authService, companyService } from "./services/api";

// Admin Components - Use lazy loading to prevent loading on login page
import Home from "./components/Admin/Home";
import Navbar from "./components/Admin/Navbar";
import Sidebar from "./components/Admin/Sidebar";
import LoginPage from "./components/auth/LoginPage";

// Lazy load heavy components that shouldn't load on login page
const Attendance = lazy(() => import("./components/Admin/Attendance"));
const Employee = lazy(() => import("./components/Admin/Employee"));
const Payroll = lazy(() => import("./components/Admin/Payroll"));
const Reports = lazy(() => import("./components/Admin/Reports"));
const GlobalSettingsModal = lazy(() => import("./components/Admin/GlobalSettingsModal"));

// Employee Components
import EmployeeAttendance from "./Employee/EmployeeAttendance";
import EmployeeHome from "./Employee/EmployeeHome";
import EmployeePayroll from "./Employee/EmployeePayroll";
import EmployeeProfile from "./Employee/EmployeeProfile";
import EmployeeReports from "./Employee/EmployeeReports";
import "./App.css";
// Removed EmployeeNavbar - using single Navbar for both admin and employee
import EmployeeSidebar from "./Employee/EmployeeSidebar";
import Toast from "./Toast";
import Masters from "./components/Admin/masters/Masters";
import AuthHandler from "./components/auth/AuthHandler"; // New component for handling authentication

function ProtectedRoute({ children, allowedUserType }) {
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
  const userType = localStorage.getItem("userType");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    } else if (userType !== allowedUserType) {
      navigate(userType === "admin" ? "/admin/home" : "/employee/home");
    }
  }, [isAuthenticated, navigate, allowedUserType, userType]);

  return isAuthenticated && userType === allowedUserType ? children : null;
}

function URLParameterHandler({ onLogin }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const companyId = urlParams.get("companyId");
    const superAdminID = urlParams.get("superAdminID");
    const password = urlParams.get("password");

    console.log(companyId, superAdminID, password);

    if (companyId && superAdminID && password) {
      // Auto-login with URL parameters
      onLogin("admin", companyId, superAdminID);

      // Clear URL parameters after login
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      // Navigate to admin home
      navigate("/admin/home");
    }
  }, [location, onLogin, navigate]);

  return null;
}

// 🆕 Auto-refresh company data on route changes
function CompanyDataRefresher() {
  const location = useLocation();
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  useEffect(() => {
    const refreshCompanyData = async () => {
      if (!isAuthenticated) return;

      try {
        const currentUser = JSON.parse(localStorage.getItem("currentUser"));
        if (currentUser?.companyId) {
          await companyService.refreshCompanyData(currentUser.companyId);
        }
      } catch (error) {
        console.warn("⚠️ Company data refresh skipped:", error);
        // Don't throw error - refresh is optional
      }
    };

    // Refresh whenever route changes
    refreshCompanyData();
  }, [location.pathname, isAuthenticated]);

  return null;
}

function App() {
  const [currentPage, setCurrentPage] = useState("Home");
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("isAuthenticated") === "true";
  });
  const [userType, setUserType] = useState(() => {
    return localStorage.getItem("userType") || "";
  });
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [companyName, setCompanyName] = useState(() => {
    const companyData = localStorage.getItem("companyData");
    return companyData ? JSON.parse(companyData).name : "";
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  // Update company name when company data changes
  useEffect(() => {
    const companyData = localStorage.getItem("companyData");
    if (companyData) {
      const company = JSON.parse(companyData);
      setCompanyName(company.name);
      // Update document title
      if (company && company.name) {
        document.title = company.name + " - PaceHRM";
      } else {
        document.title = "PaceHRM";
      }
    } else {
      document.title = "PaceHRM";
    }
  }, []);

  // Listen for localStorage changes to update title when company data changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "companyData") {
        try {
          if (e.newValue) {
            const company = JSON.parse(e.newValue);
            if (company && company.name) {
              document.title = company.name + " - PaceHRM";
              setCompanyName(company.name);
            } else {
              document.title = "PaceHRM";
              setCompanyName("");
            }
          } else {
            document.title = "PaceHRM";
            setCompanyName("");
          }
        } catch (error) {
          console.error("Error parsing company data:", error);
          document.title = "PaceHRM";
          setCompanyName("");
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode;
      localStorage.setItem("darkMode", String(newMode));
      return newMode;
    });
  }, []);

  const handleLogin = useCallback((type, companyId, superAdminID) => {
    setIsAuthenticated(true);
    setUserType(type);
    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem("userType", type);
    localStorage.setItem("companyId", companyId);
    localStorage.setItem("superAdminID", superAdminID);

    // Update company name after login
    const companyData = localStorage.getItem("companyData");
    if (companyData) {
      setCompanyName(JSON.parse(companyData).name);
    }
  }, []);

  const handleLogout = useCallback(() => {
    authService.logout();
    setIsAuthenticated(false);
    setUserType("");
    setCompanyName("");
    window.location.href = "/login"; // Force redirect to login
  }, []);

  const renderAdminContent = (pageName) => {
    const props = {
      darkMode,
      setCurrentPage,
      toggleDarkMode,
    };

    // Loading component for lazy-loaded routes
    const LoadingFallback = () => (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );

    switch (pageName) {
      case "Home":
        return <Home {...props} />;
      case "Masters":
        return <Masters {...props} />;
      case "Time & Attendance":
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Attendance {...props} />
          </Suspense>
        );
      case "Employee":
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Employee {...props} />
          </Suspense>
        );
      case "Payroll":
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Payroll {...props} />
          </Suspense>
        );
      case "Reports":
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Reports {...props} />
          </Suspense>
        );
      case "Global Settings":
        return (
          <Suspense fallback={<LoadingFallback />}>
            <GlobalSettingsModal />
          </Suspense>
        );
      default:
        return <Home {...props} />;
    }
  };

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <Router>
        <Toast />
        <div className={`flex h-screen ${darkMode ? "dark" : ""}`}>
          <div className="flex items-center justify-center w-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">
                Processing authentication...
              </p>
            </div>
          </div>
        </div>
      </Router>
    );
  }

  return (
    <Router>
      <Toast />
      <AuthHandler
        setIsAuthenticated={setIsAuthenticated}
        setUserType={setUserType}
        setIsCheckingAuth={setIsCheckingAuth}
      />
      <URLParameterHandler onLogin={handleLogin} />
      <CompanyDataRefresher />
      <div className={`flex h-screen ${darkMode ? "dark" : ""}`}>
        {isAuthenticated && userType === "admin" && (
          <Sidebar
            setCurrentPage={setCurrentPage}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
          />
        )}
        <div className="flex flex-col flex-1 overflow-hidden">
          {isAuthenticated && (
            <>
              <Navbar
                currentPage={currentPage}
                darkMode={darkMode}
                handleLogout={handleLogout}
                isEmployeeView={userType === "employee"}
              />
            </>
          )}
          <main className="flex-1 overflow-x-hidden overflow-y-auto scrollbar-hide bg-gray-100 dark:bg-gray-900">
            <Routes>
              {/* Login Route */}
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <Navigate
                      to={
                        userType === "admin" ? "/admin/home" : "/employee/home"
                      }
                      replace
                    />
                  ) : (
                    <LoginPage onLogin={handleLogin} darkMode={darkMode} />
                  )
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute allowedUserType="admin">
                    <Routes>
                      <Route
                        path="/home"
                        element={renderAdminContent("Home")}
                      />
                      <Route
                        path="masters"
                        element={renderAdminContent("Masters")}
                      />
                      <Route
                        path="attendance"
                        element={renderAdminContent("Time & Attendance")}
                      />
                      <Route
                        path="employee"
                        element={renderAdminContent("Employee")}
                      />
                      <Route
                        path="payroll"
                        element={renderAdminContent("Payroll")}
                      />
                      <Route
                        path="reports"
                        element={renderAdminContent("Reports")}
                      />
                      <Route
                        path="global-settings"
                        element={renderAdminContent("Global Settings")}
                      />
                    </Routes>
                  </ProtectedRoute>
                }
              />

              {/* Employee Routes */}
              <Route
                path="/employee/*"
                element={
                  <ProtectedRoute allowedUserType="employee">
                    <div className="flex h-full">
                      <EmployeeSidebar
                        setCurrentPage={setCurrentPage}
                        darkMode={darkMode}
                        toggleDarkMode={toggleDarkMode}
                      />
                      <div className="flex-1 h-full">
                        <Routes>
                          <Route path="home" element={<EmployeeHome />} />
                          <Route path="profile" element={<EmployeeProfile />} />
                          <Route path="payroll" element={<EmployeePayroll />} />
                          <Route path="reports" element={<EmployeeReports />} />
                          <Route
                            path="attendance"
                            element={<EmployeeAttendance />}
                          />
                        </Routes>
                      </div>
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* Default Route */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Navigate
                      to={
                        userType === "admin" ? "/admin/home" : "/employee/home"
                      }
                      replace
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;
